import { lazy, Suspense, useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { useWallet } from '../wallet/useWallet';
import { useSession } from '../session';
import { createApiClient } from '../api';
import { createWithdraw } from '../api/endpoints/walletWithdraw';
import { config } from '../../config';
import { PaymentsContext } from './PaymentsContext';
import type { FundingMode, PaymentsContextValue } from './types';

// Lazy-loaded so Stripe wrapper code is split out of the main/seamless bundle.
const DepositScreen = lazy(() =>
  import('./DepositScreen').then((m) => ({ default: m.DepositScreen })),
);

// Lazy-loaded so the withdraw amount-entry code stays out of the main bundle
// (it only loads once a player actually starts a withdrawal).
const WithdrawScreen = lazy(() =>
  import('./WithdrawScreen').then((m) => ({ default: m.WithdrawScreen })),
);

const WithdrawSuccessScreen = lazy(() =>
  import('./WithdrawSuccessScreen').then((m) => ({ default: m.WithdrawSuccessScreen })),
);

// Shown when the payout gate needs a Stripe hosted step (KYC / Connect
// onboarding) before money can move.
const WithdrawActionRequiredScreen = lazy(() =>
  import('./WithdrawActionRequiredScreen').then((m) => ({
    default: m.WithdrawActionRequiredScreen,
  })),
);

interface DepositState {
  open: boolean;
  prefillCents: number;
  // Only a voluntary top-up (balance-pill tap) offers the Withdraw link;
  // reactive top-ups mid-purchase keep the screen focused on paying.
  withdrawable: boolean;
}

type WithdrawStep =
  | { step: 'closed' }
  | { step: 'amount' }
  | { step: 'action'; kind: 'kyc' | 'onboarding'; url: string; amountCents: number }
  | { step: 'success'; amountCents: number; expressAccountUrl?: string | undefined };

export function PaymentsProvider({
  fundingMode,
  currency,
  allowWithdrawals,
  children,
}: {
  fundingMode: FundingMode;
  currency: string;
  allowWithdrawals: boolean;
  children: ReactNode;
}) {
  const isStripeFunded = fundingMode === 'stripe';
  const canWithdraw = isStripeFunded && allowWithdrawals;
  const session = useSession();
  const wallet = useWallet();

  const client = useMemo(
    () =>
      createApiClient({
        baseUrl: config.apiUrl,
        getToken: session.getToken,
        getEmbedKey: () => session.embedKey,
      }),
    [session.getToken, session.embedKey],
  );

  const [depositState, setDepositState] = useState<DepositState>({
    open: false,
    prefillCents: 0,
    withdrawable: false,
  });
  const [withdrawStep, setWithdrawStep] = useState<WithdrawStep>({ step: 'closed' });
  const resolverRef = useRef<((value: boolean) => void) | null>(null);

  const requestTopUp = useMemo(
    () =>
      (prefillCents: number, opts?: { withdrawable?: boolean }): Promise<boolean> => {
        if (!isStripeFunded) return Promise.resolve(false);
        return new Promise<boolean>((resolve) => {
          resolverRef.current?.(false); // settle any in-flight caller before replacing
          resolverRef.current = resolve;
          setDepositState({ open: true, prefillCents, withdrawable: opts?.withdrawable ?? false });
        });
      },
    [isStripeFunded],
  );

  const handleFunded = useCallback(
    async (_balanceCents: number) => {
      // Snapshot + clear resolver before the async refresh so a concurrent
      // requestTopUp call can't stomp it between the await and the resolve.
      const resolve = resolverRef.current;
      resolverRef.current = null;
      await wallet.refresh().catch(() => {}); // non-fatal; balance is credited regardless
      resolve?.(true);
      setDepositState({ open: false, prefillCents: 0, withdrawable: false });
    },
    [wallet],
  );

  const handleClose = useCallback(() => {
    resolverRef.current?.(false);
    resolverRef.current = null;
    setDepositState({ open: false, prefillCents: 0, withdrawable: false });
  }, []);

  const requestWithdraw = useCallback(() => {
    if (!canWithdraw) return;
    setWithdrawStep({ step: 'amount' });
  }, [canWithdraw]);

  // Amount confirmed (or the player returned from a hosted step and tapped
  // Continue) → drive one `withdraw` call. It resolves to one of three GP
  // Connect-payout outcomes:
  //  - ok → the `Stripe::Transfer` was created + ledger debited; refresh the
  //    balance and advance to the success step.
  //  - a KYC / onboarding URL → open it in a new tab (best-effort; may be
  //    popup-blocked inside the nested casino iframe, so the action screen
  //    also carries a user-gesture fallback button) and park on the action
  //    step until the player finishes and retries.
  //  - a plain failure → toast (DepositScreen pattern) and close.
  const handleWithdrawProceed = useCallback(
    async (amountCents: number) => {
      const res = await createWithdraw(client, amountCents / 100);
      if (res.ok) {
        await wallet.refresh().catch(() => {}); // non-fatal; the debit already landed
        setWithdrawStep({ step: 'success', amountCents, expressAccountUrl: res.expressAccount });
      } else if (res.setupAccountUrl) {
        window.open(res.setupAccountUrl, '_blank', 'noopener,noreferrer');
        setWithdrawStep({ step: 'action', kind: 'kyc', url: res.setupAccountUrl, amountCents });
      } else if (res.onboardingUrl) {
        window.open(res.onboardingUrl, '_blank', 'noopener,noreferrer');
        setWithdrawStep({ step: 'action', kind: 'onboarding', url: res.onboardingUrl, amountCents });
      } else {
        toast.error(res.message ?? 'Failed to withdraw');
        setWithdrawStep({ step: 'closed' });
      }
    },
    [client, wallet],
  );

  const value = useMemo<PaymentsContextValue>(
    () => ({ isStripeFunded, requestTopUp, canWithdraw, requestWithdraw }),
    [isStripeFunded, requestTopUp, canWithdraw, requestWithdraw],
  );

  return (
    <PaymentsContext value={value}>
      {children}
      {depositState.open && (
        <Suspense fallback={null}>
          <DepositScreen
            client={client}
            prefillCents={depositState.prefillCents}
            currency={currency}
            onFunded={handleFunded}
            onClose={handleClose}
            onWithdraw={
              depositState.withdrawable && canWithdraw && (wallet.balance_cents ?? 0) > 0
                ? () => {
                    handleClose();
                    requestWithdraw();
                  }
                : undefined
            }
          />
        </Suspense>
      )}
      {withdrawStep.step === 'amount' && (
        <Suspense fallback={null}>
          <WithdrawScreen
            balanceCents={wallet.balance_cents ?? 0}
            onProceed={(cents) => void handleWithdrawProceed(cents)}
            onClose={() => setWithdrawStep({ step: 'closed' })}
          />
        </Suspense>
      )}
      {withdrawStep.step === 'action' && (
        <Suspense fallback={null}>
          <WithdrawActionRequiredScreen
            kind={withdrawStep.kind}
            url={withdrawStep.url}
            onContinue={() => handleWithdrawProceed(withdrawStep.amountCents)}
            onClose={() => setWithdrawStep({ step: 'closed' })}
          />
        </Suspense>
      )}
      {withdrawStep.step === 'success' && (
        <Suspense fallback={null}>
          <WithdrawSuccessScreen
            amountCents={withdrawStep.amountCents}
            expressAccountUrl={withdrawStep.expressAccountUrl}
            onClose={() => setWithdrawStep({ step: 'closed' })}
          />
        </Suspense>
      )}
    </PaymentsContext>
  );
}
